// src/components/family/ChildForm.tsx
// Shared form component for adding/editing children

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  User,
  Calendar,
  MapPin,
  Heart,
  Shield,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Plus,
  X,
  Info,
} from 'lucide-react';
import {
  childFormSchema,
  type ChildFormData,
  type Child,
  GENDER_OPTIONS,
  DELIVERY_TYPE_OPTIONS,
  BLOOD_TYPE_OPTIONS,
} from '@/features/children/types/childTypes';

// US States for select
const US_STATES = [
  { value: '', label: 'Select state...' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface ChildFormProps {
  initialData?: Partial<Child>;
  onSubmit: (data: ChildFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode?: 'add' | 'edit';
}

// Form steps
const STEPS = [
  { id: 'basic', label: 'Basic Info', icon: User },
  { id: 'birth', label: 'Birth Details', icon: Calendar },
  { id: 'medical', label: 'Medical Info', icon: Heart },
  { id: 'insurance', label: 'Insurance', icon: Shield },
];

export function ChildForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode = 'add',
}: ChildFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [newAllergy, setNewAllergy] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<ChildFormData>({
    resolver: zodResolver(childFormSchema),
    defaultValues: {
      first_name: initialData?.first_name || '',
      last_name: initialData?.last_name || '',
      nickname: initialData?.nickname || '',
      date_of_birth: initialData?.date_of_birth || '',
      gender: initialData?.gender || '',
      birth_weight_oz: initialData?.birth_weight_oz || undefined,
      birth_length_inches: initialData?.birth_length_inches || undefined,
      gestational_age_weeks: initialData?.gestational_age_weeks || undefined,
      birth_hospital: initialData?.birth_hospital || '',
      birth_city: initialData?.birth_city || '',
      birth_state: initialData?.birth_state || '',
      delivery_type: initialData?.delivery_type || '',
      blood_type: initialData?.blood_type || '',
      allergies: initialData?.allergies || [],
      insurance_provider: initialData?.insurance_provider || '',
      insurance_policy_number: initialData?.insurance_policy_number || '',
      medicaid_id: initialData?.medicaid_id || '',
      notes: initialData?.notes || '',
    },
    mode: 'onChange',
  });

  const allergies = watch('allergies') || [];

  const addAllergy = () => {
    if (newAllergy.trim() && !allergies.includes(newAllergy.trim())) {
      setValue('allergies', [...allergies, newAllergy.trim()]);
      setNewAllergy('');
    }
  };

  const removeAllergy = (allergy: string) => {
    setValue(
      'allergies',
      allergies.filter((a) => a !== allergy)
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addAllergy();
    }
  };

  const goNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    if (currentStep === 0) {
      return watch('first_name') && watch('last_name') && watch('date_of_birth');
    }
    return true;
  };

  const inputClasses = (hasError: boolean) =>
    `w-full px-4 py-3 rounded-xl bg-cream border ${
      hasError ? 'border-red-400 focus:border-red-500' : 'border-transparent focus:border-sage'
    } focus:ring-2 focus:ring-sage/10 outline-none transition-all text-navy placeholder:text-warm-gray`;

  const labelClasses = 'block text-sm font-medium text-navy-light mb-2';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
      {/* Progress Steps */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-warm-gray/10">
        {STEPS.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => setCurrentStep(index)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                isActive
                  ? 'bg-sage/10 text-sage-dark'
                  : isCompleted
                  ? 'text-sage'
                  : 'text-warm-gray hover:text-navy'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isActive
                    ? 'bg-sage text-white'
                    : isCompleted
                    ? 'bg-sage/20 text-sage'
                    : 'bg-cream text-warm-gray'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <StepIcon className="w-4 h-4" />
                )}
              </div>
              <span className="hidden sm:inline text-sm font-medium">{step.label}</span>
            </button>
          );
        })}
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Step 1: Basic Info */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center">
                <User className="w-5 h-5 text-sage" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-navy">Basic Information</h3>
                <p className="text-sm text-warm-gray">Tell us about your child</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className={labelClasses}>
                  First Name <span className="text-terracotta">*</span>
                </label>
                <input
                  {...register('first_name')}
                  type="text"
                  placeholder="Enter first name"
                  className={inputClasses(!!errors.first_name)}
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.first_name.message}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className={labelClasses}>
                  Last Name <span className="text-terracotta">*</span>
                </label>
                <input
                  {...register('last_name')}
                  type="text"
                  placeholder="Enter last name"
                  className={inputClasses(!!errors.last_name)}
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.last_name.message}
                  </p>
                )}
              </div>
            </div>

            {/* Nickname */}
            <div>
              <label className={labelClasses}>Nickname (optional)</label>
              <input
                {...register('nickname')}
                type="text"
                placeholder="What do you call them?"
                className={inputClasses(!!errors.nickname)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date of Birth */}
              <div>
                <label className={labelClasses}>
                  Date of Birth <span className="text-terracotta">*</span>
                </label>
                <input
                  {...register('date_of_birth')}
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  className={inputClasses(!!errors.date_of_birth)}
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.date_of_birth.message}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div>
                <label className={labelClasses}>Gender</label>
                <select {...register('gender')} className={inputClasses(false)}>
                  <option value="">Select gender...</option>
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Birth Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-terracotta/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-navy">Birth Details</h3>
                <p className="text-sm text-warm-gray">Optional information about birth</p>
              </div>
            </div>

            <div className="p-4 bg-cream/50 rounded-xl border border-warm-gray/10">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                <p className="text-sm text-warm-gray">
                  Birth details help us provide more accurate resources and can be important for
                  medical and legal documentation. All fields are optional.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Birth Weight */}
              <div>
                <label className={labelClasses}>Birth Weight (oz)</label>
                <input
                  {...register('birth_weight_oz')}
                  type="number"
                  placeholder="e.g., 120"
                  className={inputClasses(false)}
                />
                <p className="mt-1 text-xs text-warm-gray">7 lbs 8 oz = 120 oz</p>
              </div>

              {/* Birth Length */}
              <div>
                <label className={labelClasses}>Birth Length (inches)</label>
                <input
                  {...register('birth_length_inches')}
                  type="number"
                  step="0.1"
                  placeholder="e.g., 20.5"
                  className={inputClasses(false)}
                />
              </div>

              {/* Gestational Age */}
              <div>
                <label className={labelClasses}>Gestational Age (weeks)</label>
                <input
                  {...register('gestational_age_weeks')}
                  type="number"
                  min="20"
                  max="45"
                  placeholder="e.g., 38"
                  className={inputClasses(false)}
                />
              </div>
            </div>

            {/* Delivery Type */}
            <div>
              <label className={labelClasses}>Delivery Type</label>
              <select {...register('delivery_type')} className={inputClasses(false)}>
                <option value="">Select delivery type...</option>
                {DELIVERY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Birth Location */}
            <div>
              <label className={labelClasses}>Birth Hospital</label>
              <input
                {...register('birth_hospital')}
                type="text"
                placeholder="Hospital name"
                className={inputClasses(false)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>City</label>
                <input
                  {...register('birth_city')}
                  type="text"
                  placeholder="City"
                  className={inputClasses(false)}
                />
              </div>
              <div>
                <label className={labelClasses}>State</label>
                <select {...register('birth_state')} className={inputClasses(false)}>
                  {US_STATES.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Medical Info */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-navy">Medical Information</h3>
                <p className="text-sm text-warm-gray">Important health details</p>
              </div>
            </div>

            {/* Blood Type */}
            <div>
              <label className={labelClasses}>Blood Type</label>
              <select {...register('blood_type')} className={inputClasses(false)}>
                <option value="">Select blood type...</option>
                {BLOOD_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Allergies */}
            <div>
              <label className={labelClasses}>Allergies</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newAllergy}
                  onChange={(e) => setNewAllergy(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add allergy and press Enter"
                  className={inputClasses(false)}
                />
                <button
                  type="button"
                  onClick={addAllergy}
                  disabled={!newAllergy.trim()}
                  className="px-4 py-3 rounded-xl bg-sage text-white font-medium hover:bg-sage-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              {allergies.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {allergies.map((allergy) => (
                    <span
                      key={allergy}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-sm"
                    >
                      {allergy}
                      <button
                        type="button"
                        onClick={() => removeAllergy(allergy)}
                        className="hover:text-red-900 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className={labelClasses}>Additional Notes</label>
              <textarea
                {...register('notes')}
                rows={4}
                placeholder="Any other important medical information..."
                className={`${inputClasses(false)} resize-none`}
              />
            </div>
          </div>
        )}

        {/* Step 4: Insurance */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-navy">Insurance Information</h3>
                <p className="text-sm text-warm-gray">Health coverage details</p>
              </div>
            </div>

            <div className="p-4 bg-cream/50 rounded-xl border border-warm-gray/10">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                <p className="text-sm text-warm-gray">
                  Insurance information helps streamline provider visits and expense tracking.
                  You can update this anytime.
                </p>
              </div>
            </div>

            {/* Insurance Provider */}
            <div>
              <label className={labelClasses}>Insurance Provider</label>
              <input
                {...register('insurance_provider')}
                type="text"
                placeholder="e.g., Blue Cross Blue Shield"
                className={inputClasses(false)}
              />
            </div>

            {/* Policy Number */}
            <div>
              <label className={labelClasses}>Policy Number</label>
              <input
                {...register('insurance_policy_number')}
                type="text"
                placeholder="Policy or member ID"
                className={inputClasses(false)}
              />
            </div>

            {/* Medicaid ID */}
            <div>
              <label className={labelClasses}>Medicaid ID (if applicable)</label>
              <input
                {...register('medicaid_id')}
                type="text"
                placeholder="Medicaid member ID"
                className={inputClasses(false)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-warm-gray/10 bg-cream/30">
        <button
          type="button"
          onClick={currentStep === 0 ? onCancel : goPrev}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-navy hover:bg-cream transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </button>

        {currentStep < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canProceed()}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-sage text-white font-medium hover:bg-sage-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-sage to-sage-dark text-white font-medium shadow-lg shadow-sage/20 hover:shadow-xl hover:shadow-sage/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              <>
                {mode === 'add' ? 'Add Child' : 'Save Changes'}
              </>
            )}
          </button>
        )}
      </div>
    </form>
  );
}

export default ChildForm;

